import { useState, useEffect } from 'react';
import api from '../../lib/api';

interface Win {
  id: string;
  timeframe: string;
  title: string;
  description: string;
  progress: number;
  currentValue: number | null;
  targetValue: number | null;
}

interface Props {
  mundusId: string;
  onAction: (message: string) => void;
}

export default function DailyChallenge({ mundusId, onAction }: Props) {
  const [challenge, setChallenge] = useState<Win | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadChallenge();
  }, [mundusId]);

  const loadChallenge = async () => {
    try {
      const res = await api.get(`/api/mundus/${mundusId}/wins`);
      const activeWins: Win[] = res.data.activeWins || [];
      // Pick the most urgent: QUICK first, then DAILY
      const urgent = activeWins.find(w => w.timeframe === 'QUICK')
        || activeWins.find(w => w.timeframe === 'DAILY')
        || activeWins[0];
      setChallenge(urgent || null);
    } catch {
      // No wins
    } finally {
      setLoading(false);
    }
  };

  if (loading || !challenge) return null;

  // If already complete, don't show
  if (challenge.progress >= 100) return null;

  const remaining = (challenge.targetValue || 1) - (challenge.currentValue || 0);
  const isClose = challenge.progress >= 60;

  return (
    <div className={`rounded-xl border p-4 ${
      isClose
        ? 'bg-amber-50 border-amber-200'
        : 'bg-blue-50 border-blue-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">{challenge.timeframe === 'QUICK' ? '🎯' : '⭐'}</span>
            <span className="text-xs font-semibold uppercase text-slate-500">
              {challenge.timeframe === 'QUICK' ? "Today's Challenge" : 'Daily Win'}
            </span>
            {isClose && (
              <span className="text-xs bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full font-medium">
                Almost there!
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-slate-900">{challenge.title}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {remaining} more to go &middot; {Math.round(challenge.progress)}% done
          </p>
        </div>
        <button
          onClick={() => onAction(`Help me with my challenge: ${challenge.title}`)}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
            isClose
              ? 'bg-amber-600 text-white hover:bg-amber-700'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          Do it now →
        </button>
      </div>

      {/* Mini progress bar */}
      <div className="w-full bg-white/50 rounded-full h-1 mt-3">
        <div
          className={`h-1 rounded-full transition-all ${isClose ? 'bg-amber-500' : 'bg-blue-500'}`}
          style={{ width: `${Math.min(challenge.progress, 100)}%` }}
        />
      </div>
    </div>
  );
}
